import axios from "axios";
import { getAuthToken } from "../auth/AuthContext";

export const api = axios.create({
  baseURL: "/api",
  timeout: 300000, // 5 minutes timeout for large file uploads
  maxContentLength: 50 * 1024 * 1024, // 50MB max content length
  maxBodyLength: 50 * 1024 * 1024, // 50MB max body length
  withCredentials: true, // Send session cookie for staff auth
});

// Extend axios config to include auth type metadata
declare module 'axios' {
  export interface AxiosRequestConfig {
    useVendorAuth?: boolean; // Explicitly specify to use vendor token
  }
}

// Staff auth: session cookie (or in dev, Bearer from demo login). Vendor auth: Bearer token.
api.interceptors.request.use(cfg => {
  const vendorToken = localStorage.getItem('hmh-vendor-token');
  const staffToken = getAuthToken();
  const isVendorRoute =
    cfg.useVendorAuth === true ||
    cfg.url?.startsWith('/vendors/profile') ||
    cfg.url?.startsWith('/vendors/documents') ||
    cfg.url?.startsWith('/vendors/tenders/') ||
    cfg.url?.startsWith('/vendors/bid-submissions') ||
    cfg.url?.startsWith('/vendors/categories/available') ||
    cfg.url?.startsWith('/vendors/profile/categories') ||
    cfg.url?.startsWith('/vendors/login') ||
    cfg.url?.startsWith('/vendors/register');

  if (isVendorRoute && vendorToken) {
    cfg.headers.Authorization = `Bearer ${vendorToken}`;
  } else if (!isVendorRoute && staffToken) {
    cfg.headers.Authorization = `Bearer ${staffToken}`;
  }
  // Staff with no token: session cookie is sent via withCredentials

  if (cfg.data instanceof FormData && cfg.headers['Content-Type']) {
    delete cfg.headers['Content-Type'];
  }

  return cfg;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const isVendorRoute = error.config?.url?.startsWith('/vendors/');
      const url = typeof error.config?.url === 'string' ? error.config.url : '';
      const isAuthCheck = url === '/auth/me' || url.endsWith('/auth/me');
      const isDevLogin = url === '/auth/login' || url.endsWith('/auth/login');
      // Don't redirect for auth bootstrap/login — avoids reload loops and surfaces login errors.
      if (!isVendorRoute && !isAuthCheck && !isDevLogin) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
); 